import type { ITestIdeaPriority, ITestIdeaPriorityStyle } from '../types';
import type { BoxProps } from '@shopify/polaris';

import { EVENT_ANALYTIC } from '~/modules/tracking';

export const TEST_IDEA_PRIORITY = {
  HIGH: 'high',
  MEDIUM: 'medium',
} as const;

export const TEST_IDEA_TYPE = {
  TEMPLATE: 'Template testing',
  PRICE: 'Price testing',
} as const;

export const TEST_IDEA_PRIORITY_STYLE: Record<ITestIdeaPriority, ITestIdeaPriorityStyle> = {
  [TEST_IDEA_PRIORITY.HIGH]: {
    label: 'High',
    badgeTone: 'critical',
    suggestionBackground: 'bg-surface-critical',
    suggestionIconTone: 'critical',
  },
  [TEST_IDEA_PRIORITY.MEDIUM]: {
    label: 'Medium',
    badgeTone: 'attention',
    suggestionBackground: 'bg-surface-caution',
    suggestionIconTone: 'caution',
  },
};

export const TEST_IDEA_SECTION_HEADING_SEPARATOR = '·';
export const TEST_IDEA_DETAIL_CLOSE_KEY = 'Escape';

// The design shows the toggle's tooltip after half a second of hover.
export const TEST_IDEAS_TOGGLE_TOOLTIP_DELAY = 500;
// The tooltip anchors to this wrapper; left inline it is shorter, so the tooltip covers the button.
export const TEST_IDEAS_TOGGLE_ACTIVATOR_CLASS = 'inline-flex';
// Stored once the sidebar has been closed in this browser, so the toggle hint shows only once.
export const TEST_IDEAS_TOGGLE_HINT_SEEN_VALUE = 'true';

export const TEST_IDEA_DETAIL_STEP = {
  PROBLEM: 1,
  HYPOTHESIS: 2,
  TEST_IDEA: 3,
  EXPECTED_RESULT: 4,
} as const;

// 6px scrollbar (design); Chrome skips ::-webkit-scrollbar while Polaris' scrollbar-* are set.
export const TEST_IDEAS_THIN_SCROLL_CLASS =
  'overflow-y-auto overscroll-contain [scrollbar-color:auto] [scrollbar-width:auto] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--p-color-scrollbar-thumb-bg)] [&::-webkit-scrollbar-thumb:hover]:bg-[var(--p-color-scrollbar-thumb-bg-hover)]';

// Pinned to the app frame so the document itself never scrolls, only the two columns do.
export const TEST_IDEAS_FRAME_CLASS = 'fixed inset-0 flex justify-center bg-[var(--p-color-bg)]';
// The design's 1424px frame (1104px report + 320px sidebar), centred on wide screens.
export const TEST_IDEAS_FRAME_INNER_CLASS = 'relative flex w-[1424px] max-w-full overflow-hidden';
export const TEST_IDEAS_PAGE_COLUMN_CLASS =
  'relative min-w-0 flex-1 bg-[var(--p-color-bg-surface)]';
// Polaris popovers otherwise measure the 0px-tall <body> and close at once as scrolled out.
export const TEST_IDEAS_PAGE_SCROLLABLE_PROPS = { 'data-polaris-scrollable': true } as const;
// 16px on the left, 16px minus the 6px scrollbar on the right, so cards stay 288px wide.
export const TEST_IDEAS_SIDEBAR_COLUMN_CLASS =
  'h-full w-80 shrink-0 bg-[var(--p-color-bg-surface-secondary)] pb-4 pl-4 pr-2.5';
// The sidebar opens on mount only from 1040px (TestIdeasProvider), so its skeleton shows there.
export const TEST_IDEAS_SIDEBAR_SKELETON_CLASS = 'hidden overflow-hidden min-[1040px]:block';

// Below lg (1040px) the sidebar overlays the report; classes repeat 1040px, keep them in sync.
export const TEST_IDEAS_SIDE_BY_SIDE_MEDIA_QUERY = '(min-width: 1040px)';
// z-[320]: above the sticky filter row (310), under the sidebar it dims the report for.
export const TEST_IDEAS_SIDEBAR_SCRIM_CLASS =
  'absolute inset-0 z-[320] bg-[var(--p-color-backdrop-bg)] transition-opacity duration-200 min-[1040px]:hidden';
export const TEST_IDEAS_SIDEBAR_SCRIM_OPEN_CLASS = 'opacity-100';
export const TEST_IDEAS_SIDEBAR_SCRIM_CLOSED_CLASS = 'pointer-events-none opacity-0';
// z-[340]: above its scrim (320), below the idea drawer and its scrim (350).
export const TEST_IDEAS_SIDEBAR_OVERLAY_CLASS =
  'absolute inset-y-0 right-0 z-[340] shadow-[var(--p-shadow-600)] transition-[transform,visibility] duration-300 min-[1040px]:static min-[1040px]:z-auto min-[1040px]:shadow-none min-[1040px]:transition-none';
export const TEST_IDEAS_SIDEBAR_OPEN_CLASS = 'visible translate-x-0';
export const TEST_IDEAS_SIDEBAR_CLOSED_CLASS = 'invisible translate-x-full min-[1040px]:hidden';

/** Pins the sidebar's "A/B test ideas" header to its top while the cards scroll under it. */
export const TEST_IDEAS_SIDEBAR_HEADER_STICKY: BoxProps = {
  position: 'sticky',
  insetBlockStart: '0',
  zIndex: '1',
  background: 'bg-surface-secondary',
  paddingBlock: '400',
};

export const TEST_IDEA_CARD_BOX: BoxProps = {
  background: 'bg-surface',
  borderWidth: '025',
  borderColor: 'border',
  borderRadius: '300',
  padding: '400',
};

/** The sidebar while the demo loads: as many cards as fill its first screen, shaped like one. */
export const TEST_IDEAS_SKELETON_CARD_KEYS = [0, 1, 2] as const;
export const TEST_IDEA_CARD_SKELETON = {
  TAGS_WIDTH: '50%',
  CLAIM_LINES: 2,
  LINK_WIDTH: '30%',
  // The suggestion box's 56px (see TestIdeaSuggestion).
  SUGGESTION_HEIGHT: '56px',
} as const;

// The demo iframe has no plan data, so its events go to the app window, which tracks them.
export const TEST_IDEAS_TRACKING_CHANNEL_PREFIX = 'gemx-test-ideas-tracking-';
export const TEST_IDEAS_TRACK_MESSAGE_TYPE = 'TEST_IDEAS_TRACK_EVENT';
export const TEST_IDEAS_TRACKED_EVENT = {
  IDEA_VIEW_DETAIL: EVENT_ANALYTIC.GEMX_ANALYTICS_DEMO_IDEA_VIEW_DETAIL,
  IDEAS_SIDEBAR_CLOSE: EVENT_ANALYTIC.GEMX_ANALYTICS_DEMO_IDEAS_SIDEBAR_CLOSE,
} as const;
